// ============================================================
// CONVERSATION CONTEXT (short-term memory across turns)
// ============================================================
// Extends the existing planner with continuity — NOT a redesign of how it
// understands language. After each turn we derive a small structured
// summary of what was just discussed (measure, dimension, filters, time
// period, entities, intent) and hand it back to the planner alongside the
// schema on the NEXT turn, so a follow-up like "what about 2013?" or
// "compare her with Ashish" can be resolved without the user repeating
// themselves.
//
// This is pure bookkeeping: it never talks to Groq and never touches the
// dataset. New values overwrite old ones per-field; anything the latest
// turn didn't touch is carried forward untouched, since a follow-up
// question usually only changes ONE thing (the time period, an entity,
// etc.) and means to keep the rest.

export function emptyContext() {
  return {
    measure: null,
    dimension: null,
    filters: [],
    timePeriod: null,
    entities: [],
    intent: null,
  };
}

// Pulls entity-like values (e.g. rep/category names) out of the filters
// and comparisons a plan actually used, so "compare her with Ashish" can
// resolve "her" against whoever was just being discussed.
function extractEntities(plan, toolResult) {

  const entities = [];

  for (const step of plan?.steps || []) {
    if (step.tool === "filter" && typeof step.value === "string" && !step.datePart) {
      entities.push(step.value);
    }
    if (step.tool === "compare") {
      if (step.valueA) entities.push(step.valueA);
      if (step.valueB) entities.push(step.valueB);
    }
  }

  if (toolResult?.tool === "table" && toolResult.isGrouped && toolResult.data?.[0]?.group) {
    entities.push(String(toolResult.data[0].group));
  }

  return entities;
}

function extractIntent(plan) {
  const tools = (plan?.steps || []).map(s => s.tool);
  if (tools.includes("driver")) return "driver";
  if (tools.includes("trend")) return "trend";
  if (tools.includes("compare")) return "compare";
  if (tools.includes("chart")) return "chart";
  if (tools.includes("groupBy")) return "breakdown";
  if (tools.some(t => ["top", "bottom"].includes(t))) return "ranking";
  return "aggregate";
}

export function updateContext(prevContext, plan, toolResult) {

  const prev = prevContext || emptyContext();

  if (!plan?.steps?.length) return prev; // chit-chat turn — leave context untouched

  const measureStep = plan.steps.find(s => s.measure);
  const dimensionStep = plan.steps.find(s => s.dimension);
  const timeStep = plan.steps.find(s => s.tool === "filter" && s.datePart) ||
                    plan.steps.find(s => s.tool === "trend");

  const filters = plan.steps
    .filter(s => s.tool === "filter")
    .map(s => ({ column: s.column, operator: s.operator || "=", value: s.value, datePart: s.datePart || null }));

  const newEntities = extractEntities(plan, toolResult);

  return {
    measure: measureStep?.measure || prev.measure,
    dimension: dimensionStep?.dimension || prev.dimension,
    filters: filters.length ? filters : prev.filters,
    timePeriod: timeStep
      ? (timeStep.value ?? timeStep.period ?? prev.timePeriod)
      : prev.timePeriod,
    entities: newEntities.length ? newEntities : prev.entities,
    intent: extractIntent(plan) || prev.intent,
  };
}

// Formats context as a short, readable block for the planner prompt.
export function describeContext(context) {

  if (!context || (!context.measure && !context.dimension && !context.entities?.length)) {
    return "None yet — this is the first analytical question in this conversation.";
  }

  const parts = [];

  if (context.measure) parts.push(`Measure: ${context.measure}`);
  if (context.dimension) parts.push(`Dimension: ${context.dimension}`);
  if (context.filters?.length) parts.push(`Filters: ${JSON.stringify(context.filters)}`);
  if (context.timePeriod) parts.push(`Time period: ${context.timePeriod}`);
  if (context.entities?.length) parts.push(`Entities discussed: ${context.entities.join(", ")}`);
  if (context.intent) parts.push(`Last analysis type: ${context.intent}`);

  return parts.join("\n");
}
